import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [editUser, setEditUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user', institution: '', is_active: true });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      const { data } = await api.get('/admin/users', { params });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search, filterRole]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editUser.id}`, {
        name: editUser.name,
        email: editUser.email,
        institution: editUser.institution,
        password: editUser.password, // Optional
        role: editUser.role,
        is_active: editUser.is_active
      });
      toast.success('Pengguna diperbarui');
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${deleteConfirm.id}`);
      toast.success('Pengguna dihapus');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal'); }
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.name || !newUser.email || !newUser.password) {
        return toast.error('Nama, email, dan masukan sandi (password) wajib diisi.');
      }
      await api.post('/admin/users', newUser);
      toast.success('Pengguna berhasil ditambahkan!');
      setShowAddModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'user', institution: '', is_active: true });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Gagal menambahkan pengguna.'); }
  };

  const roleLabels = { admin: 'Administrator', expert: 'Ahli PBJ', user: 'Pengguna' };
  const roleBadge = { admin: 'badge-red', expert: 'badge-orange', user: 'badge-blue' };

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Kelola Pengguna</h2>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>+ Tambah Pengguna</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '1 1 250px' }}>
            <label className="form-label">Cari</label>
            <input className="form-input" placeholder="Nama, email, instansi..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchUsers()} />
          </div>
          <div className="form-group" style={{ flex: '0 1 160px' }}>
            <label className="form-label">Role</label>
            <select className="form-select" value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}>
              <option value="">Semua</option>
              <option value="admin">Admin</option>
              <option value="expert">Ahli</option>
              <option value="user">Pengguna</option>
            </select>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setPage(1); fetchUsers(); }}>Cari</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /><span>Memuat...</span></div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>Instansi</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Login Terakhir</th>
                  <th style={{ width: 120 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 600 }}>{u.name}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem' }}>{u.email}</td>
                    <td>{u.institution || '-'}</td>
                    <td><span className={`badge ${roleBadge[u.role]}`}>{roleLabels[u.role]}</span></td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`}>
                        {u.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                      {u.last_login ? new Date(u.last_login).toLocaleDateString('id-ID') : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm btn-icon" title="Edit" onClick={() => setEditUser({ ...u })}>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                        </button>
                        <button className="btn btn-ghost btn-sm btn-icon" title="Hapus" onClick={() => setDeleteConfirm(u)} style={{ color: 'var(--error)' }}>
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.pages > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&laquo;</button>
              {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => i + 1).map(p => (
                <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>&raquo;</button>
            </div>
          )}
        </>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Tambah Pengguna Baru</h2>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Nama Lengkap</label>
              <input type="text" className="form-input" value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Password</label>
              <input type="password" className="form-input" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Minimal 6 karakter" />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Instansi / Unit Kerja</label>
              <input type="text" className="form-input" value={newUser.institution} onChange={e => setNewUser(p => ({ ...p, institution: e.target.value }))} placeholder="Mis: Dinas Kominfo" />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Role</label>
              <select className="form-select" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                <option value="user">Pengguna</option>
                <option value="expert">Ahli PBJ</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleCreateUser}>Tambah</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">Edit Pengguna</h2>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Nama Lengkap</label>
              <input type="text" className="form-input" value={editUser.name} onChange={e => setEditUser(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={editUser.email} onChange={e => setEditUser(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Password Baru (Opsional)</label>
              <input type="password" className="form-input" value={editUser.password || ''} onChange={e => setEditUser(p => ({ ...p, password: e.target.value }))} placeholder="Kosongkan jika tidak ingin mengubah password" />
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Instansi / Unit Kerja</label>
              <input type="text" className="form-input" value={editUser.institution || ''} onChange={e => setEditUser(p => ({ ...p, institution: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Role</label>
              <select className="form-select" value={editUser.role} onChange={e => setEditUser(p => ({ ...p, role: e.target.value }))}>
                <option value="user">Pengguna</option>
                <option value="expert">Ahli PBJ</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 24 }}>
              <label className="form-label">Status</label>
              <select className="form-select" value={editUser.is_active ? 'true' : 'false'} onChange={e => setEditUser(p => ({ ...p, is_active: e.target.value === 'true' }))}>
                <option value="true">Aktif</option>
                <option value="false">Nonaktif</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEditUser(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleUpdateUser}>Simpan</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 className="modal-title" style={{ color: 'var(--error)' }}>Hapus Pengguna</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 24 }}>
              Yakin ingin menghapus <strong>{deleteConfirm.name}</strong>? Tindakan ini tidak dapat dibatalkan.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Batal</button>
              <button className="btn btn-danger" onClick={handleDeleteUser}>Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
